import type { HeadContextType, HeadTagGroup } from './HeadContext.ts';
import { useCallback, useEffect, useRef, useState } from 'react';
import { HeadContext } from './HeadContext.ts';

export function HeadProvider({ children }: React.PropsWithChildren) {
  const [groups, setGroups] = useState<HeadTagGroup[]>([]);

  // On the server, React only does one synchronous render and ignores any state
  // updates, so we use refs to capture state changes instead of trying to flush
  // setState.
  const groupsRef = useRef<HeadTagGroup[]>([]);

  // React no longer keeps track of head tags rendered on the server, so we need
  // to delete them manually on the client.
  useEffect(() => {
    document.querySelectorAll('head [data-ssr]').forEach(el => el.remove());
  }, []);

  const setGroup = useCallback<HeadContextType['setGroup']>((id, tags) => {
    if (typeof window !== 'undefined') {
      setGroups(prevGroups => {
        const group = { id, tags: [...tags] };

        const index = prevGroups.findIndex(g => g.id === id);
        if (index !== -1) {
          prevGroups[index] = group;
          return [...prevGroups];
        }
        return [...prevGroups, group];
      });
    } else {
      const record = groupsRef.current.find(r => r.id === id);
      if (record) {
        record.tags = [...tags];
      } else {
        groupsRef.current.push({ id, tags: [...tags] });
      }
    }
  }, []);

  const removeGroup = useCallback<HeadContextType['removeGroup']>((id: string) => {
    if (typeof window !== 'undefined') {
      setGroups(prevGroups => {
        const index = prevGroups.findIndex(g => g.id === id);
        if (index !== -1) {
          // TODO: Use toSpliced() once it has gained wider browser support
          return [...prevGroups].splice(index, 1);
        }
        return prevGroups;
      });
    } else {
      const index = groupsRef.current.findIndex(g => g.id === id);
      if (index !== -1) {
        groupsRef.current.splice(index, 1);
      }
    }
  }, []);

  return (
    <HeadContext
      value={{
        groups: typeof window !== 'undefined' ? groups : groupsRef.current,
        setGroup,
        removeGroup
      }}
    >
      {children}
    </HeadContext>
  );
}
